<template>
  <div>
    <!-- Header e navegação -->
    <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pt-8 pb-12">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-3">Integrações</h1>
        <p class="text-[#9CA3AF] leading-relaxed text-sm">Conecte suas ferramentas favoritas e automatize seus fluxos de trabalho.</p>
      </div>

      <!-- Grid de integrações -->
      <div class="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
        <div
          v-for="integration in visibleIntegrations"
          :key="integration.provider"
          class="bg-white/5 rounded-xl overflow-hidden transition-all duration-200"
        >
          <div class="flex p-6">
            <div class="flex-shrink-0 mr-6">
              <img :src="integration.logo" :alt="integration.name" class="w-20 h-20 object-contain" />
            </div>
            <div class="flex flex-col justify-between">
              <div>
                <h3 class="text-xl font-semibold text-white mb-1">{{ integration.name }}</h3>
                <p class="text-sm text-zinc-400">{{ integration.description }}</p>
              </div>
              <button
                @click="openIntegrationDialog(integration)"
                class="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors duration-200 text-sm font-medium"
              >
                {{ integration.connected ? 'Gerenciar conexão' : 'Vincular conta' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:open="showDialog">
      <DialogScrollContent
        :class="[
          'transition-all duration-300',
          selectedIntegration?.provider === IntegrationProviderEnum.TWITTER || selectedIntegration?.provider === IntegrationProviderEnum.TIKTOK || selectedIntegration?.provider === IntegrationProviderEnum.KWAI ? 'sm:max-w-lg' : 'sm:max-w-md'
        ]"
      >
        <DialogHeader>
          <DialogTitle>Integre com o {{ selectedIntegration?.name }}</DialogTitle>
        </DialogHeader>
        <div
          v-if="!isLoading"
          class="w-full"
          @click.stop
        >
          <form @submit.prevent="handleSave">
            <div class="grid grid-cols-1 gap-5 pt-4">

              <!-- ── Tutorial X/Twitter ─────────────────────────────── -->
              <div v-if="selectedIntegration?.provider === IntegrationProviderEnum.TWITTER" class="space-y-2">

                <!-- Toggle button -->
                <button
                  type="button"
                  @click="showXTutorial = !showXTutorial"
                  class="x-tutorial-toggle w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 group"
                  :class="showXTutorial ? 'x-tutorial-toggle--open' : ''"
                >
                  <div class="flex items-center gap-2.5">
                    <svg class="w-4 h-4 text-zinc-400 flex-shrink-0 group-hover:text-emerald-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span class="text-sm font-medium text-zinc-300 group-hover:text-white transition-colors duration-200">
                      Como obter o Access Token e o Pixel ID?
                    </span>
                  </div>
                  <svg
                    class="w-4 h-4 text-zinc-500 flex-shrink-0 transition-transform duration-200"
                    :class="showXTutorial ? 'rotate-180' : ''"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <!-- Steps panel -->
                <Transition name="x-tutorial">
                  <div
                    v-if="showXTutorial"
                    class="rounded-xl border border-white/[0.07] bg-zinc-900/70 overflow-hidden"
                  >
                    <div class="max-h-[380px] overflow-y-auto x-steps-scroll px-4 py-4">
                      <div
                        v-for="(step, index) in xTutorialSteps"
                        :key="index"
                        class="flex gap-3.5"
                      >
                        <!-- Number + connector -->
                        <div class="flex flex-col items-center flex-shrink-0">
                          <div
                            class="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                            :class="index === xTutorialSteps.length - 1
                              ? 'bg-emerald-500/15 border border-emerald-500/30'
                              : 'bg-zinc-800 border border-white/[0.09]'"
                          >
                            <span
                              class="text-[10px] font-mono font-bold tabular-nums"
                              :class="index === xTutorialSteps.length - 1 ? 'text-emerald-400' : 'text-zinc-400'"
                            >{{ String(index + 1).padStart(2, '0') }}</span>
                          </div>
                          <div
                            v-if="index < xTutorialSteps.length - 1"
                            class="x-step-connector flex-1 w-px mt-1.5"
                          ></div>
                        </div>

                        <!-- Content -->
                        <div
                          class="flex-1 min-w-0"
                          :class="index < xTutorialSteps.length - 1 ? 'pb-5' : 'pb-0.5'"
                        >
                          <p class="text-sm font-semibold text-white leading-snug mb-1">{{ step.title }}</p>
                          <p class="text-xs text-zinc-400 leading-relaxed mb-3">{{ step.description }}</p>

                          <!-- Screenshot card -->
                          <button
                            type="button"
                            @click="lightboxImage = step.image"
                            class="x-screenshot-btn w-full text-left rounded-lg overflow-hidden border border-white/[0.07] transition-all duration-200 group cursor-zoom-in block"
                          >
                            <!-- Fake browser chrome -->
                            <div class="bg-zinc-800/90 px-3 py-2 flex items-center gap-1.5 border-b border-white/[0.06]">
                              <div class="w-2 h-2 rounded-full bg-zinc-600/80"></div>
                              <div class="w-2 h-2 rounded-full bg-zinc-600/80"></div>
                              <div class="w-2 h-2 rounded-full bg-zinc-600/80"></div>
                              <span class="ml-2 text-[10px] font-mono text-zinc-500">ads.x.com</span>
                              <span class="ml-auto text-[10px] text-emerald-400/70 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                ampliar ↗
                              </span>
                            </div>
                            <!-- Image -->
                            <div class="bg-zinc-950 overflow-hidden relative">
                              <img
                                :src="step.image"
                                :alt="step.title"
                                class="w-full h-auto object-cover block transition-transform duration-300 group-hover:scale-[1.012]"
                                loading="lazy"
                              />
                              <div class="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"></div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>
              <!-- ── /Tutorial X ────────────────────────────────────── -->

              <div v-for="field in selectedIntegration?.fields" :key="field.id" class="space-y-2">
                <div class="flex items-center justify-between">
                  <Label :for="field.id" class="text-sm font-medium">
                    {{ field.label }}
                  </Label>
                </div>
                <Input
                  :id="field.id"
                  :type="field.type"
                  :placeholder="field.placeholder"
                  v-model="selectedIntegration!.data[field.id]"
                  class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <!-- Seção específica para Twitter/X -->
              <div v-if="selectedIntegration?.provider === IntegrationProviderEnum.TWITTER" class="space-y-4 pt-2">

                <!-- Mensagem informativa sobre o rastreamento -->
                <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <h4 class="text-sm font-medium text-blue-300 mb-1">Configuração do Rastreamento</h4>
                      <p class="text-xs text-blue-200/80 leading-relaxed">
                        Para que o rastreamento X/Twitter funcione corretamente, é necessário utilizar o link específico do bot abaixo em suas campanhas e automações.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Campo do link do bot -->
                <div class="space-y-3">
                  <Label class="text-sm font-medium text-white">Link do Bot para Rastreamento</Label>
                  <div class="flex items-center space-x-2">
                    <div class="flex-1 relative">
                      <Input
                        :model-value="botTrackingLink"
                        readonly
                        class="pr-10 text-gray-300 cursor-pointer select-all"
                        @click="selectBotLink"
                      />
                      <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      @click="copyBotLink"
                      class="px-3 py-2 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all duration-200"
                    >
                      <svg v-if="!linkCopied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <svg v-else class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span class="ml-1.5 text-xs font-medium">
                        {{ linkCopied ? 'Copiado!' : 'Copiar' }}
                      </span>
                    </Button>
                  </div>
                  <p class="text-xs text-gray-400">
                    Use este link em suas campanhas, automações e fluxos para garantir o rastreamento correto dos eventos.
                  </p>
                </div>

                <!-- Purchase (sempre ativo) -->
                <div class="space-y-2">
                  <Label for="twitterPurchaseEventId" class="text-sm font-medium">ID do Evento Purchase</Label>
                  <Input
                    id="twitterPurchaseEventId"
                    type="text"
                    placeholder="ID do evento de Purchase no X Ads Manager"
                    v-model="selectedIntegration.data.purchaseEventId"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <!-- PageView -->
                <div class="flex items-center space-x-2">
                  <Checkbox
                    id="twitterEnablePageView"
                    v-model:checked="selectedIntegration.data.enablePageView"
                  />
                  <label
                    for="twitterEnablePageView"
                    class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                  >
                    Habilitar evento PageView
                  </label>
                </div>
                <div v-if="selectedIntegration.data.enablePageView" class="space-y-2 pl-6">
                  <Label for="twitterPageViewEventId" class="text-sm font-medium">ID do Evento PageView</Label>
                  <Input
                    id="twitterPageViewEventId"
                    type="text"
                    placeholder="ID do evento de PageView no X Ads Manager"
                    v-model="selectedIntegration.data.pageViewEventId"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <!-- InitiateCheckout -->
                <div class="flex items-center space-x-2">
                  <Checkbox
                    id="twitterEnableInitiateCheckout"
                    v-model:checked="selectedIntegration.data.enableInitiateCheckout"
                  />
                  <label
                    for="twitterEnableInitiateCheckout"
                    class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                  >
                    Habilitar evento InitiateCheckout
                  </label>
                </div>
                <div v-if="selectedIntegration.data.enableInitiateCheckout" class="space-y-2 pl-6">
                  <Label for="twitterInitiateCheckoutEventId" class="text-sm font-medium">ID do Evento InitiateCheckout</Label>
                  <Input
                    id="twitterInitiateCheckoutEventId"
                    type="text"
                    placeholder="ID do evento de InitiateCheckout no X Ads Manager"
                    v-model="selectedIntegration.data.initiateCheckoutEventId"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              <!-- Seção específica para Meta com mensagem e link do bot -->
              <div v-if="selectedIntegration?.provider === IntegrationProviderEnum.META" class="space-y-6">
                <!-- Mensagem informativa sobre o rastreamento -->
                <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <h4 class="text-sm font-medium text-blue-300 mb-1">Configuração do Rastreamento</h4>
                      <p class="text-xs text-blue-200/80 leading-relaxed">
                        Para que o rastreamento Meta funcione corretamente, é necessário utilizar o link específico do bot abaixo em suas campanhas e automações.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Campo do link do bot -->
                <div class="space-y-3">
                  <Label class="text-sm font-medium text-white">Link do Bot para Rastreamento</Label>
                  <div class="flex items-center space-x-2">
                    <div class="flex-1 relative">
                      <Input
                        :model-value="botTrackingLink"
                        readonly
                        class="pr-10 text-gray-300 cursor-pointer select-all"
                        @click="selectBotLink"
                      />
                      <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      @click="copyBotLink"
                      class="px-3 py-2 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all duration-200"
                    >
                      <svg v-if="!linkCopied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <svg v-else class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span class="ml-1.5 text-xs font-medium">
                        {{ linkCopied ? 'Copiado!' : 'Copiar' }}
                      </span>
                    </Button>
                  </div>
                  <p class="text-xs text-gray-400">
                    Use este link em suas campanhas, automações e fluxos para garantir o rastreamento correto dos eventos.
                  </p>
                </div>

                <!-- Checkboxes para eventos -->
                <div class="space-y-4 pt-2">
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      id="enablePageView"
                      v-model:checked="selectedIntegration.data.enablePageView"
                    />
                    <label
                      for="enablePageView"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                    >
                      Habilitar evento PageView
                    </label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      id="enableInitiateCheckout"
                      v-model:checked="selectedIntegration.data.enableInitiateCheckout"
                    />
                    <label
                      for="enableInitiateCheckout"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                    >
                      Habilitar evento InitiateCheckout
                    </label>
                  </div>
                </div>
              </div>

              <!-- Seção específica para TikTok com mensagem e link do bot -->
              <div v-if="selectedIntegration?.provider === IntegrationProviderEnum.TIKTOK" class="space-y-6">
                <!-- Mensagem informativa sobre o rastreamento -->
                <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <h4 class="text-sm font-medium text-blue-300 mb-1">Configuração do Rastreamento</h4>
                      <p class="text-xs text-blue-200/80 leading-relaxed">
                        Para que o rastreamento TikTok funcione corretamente, é necessário utilizar o link específico do bot abaixo em suas campanhas e automações.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Campo do link do bot -->
                <div class="space-y-3">
                  <Label class="text-sm font-medium text-white">Link do Bot para Rastreamento</Label>
                  <div class="flex items-center space-x-2">
                    <div class="flex-1 relative">
                      <Input
                        :model-value="botTrackingLink"
                        readonly
                        class="pr-10 text-gray-300 cursor-pointer select-all"
                        @click="selectBotLink"
                      />
                      <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      @click="copyBotLink"
                      class="px-3 py-2 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all duration-200"
                    >
                      <svg v-if="!linkCopied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <svg v-else class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span class="ml-1.5 text-xs font-medium">
                        {{ linkCopied ? 'Copiado!' : 'Copiar' }}
                      </span>
                    </Button>
                  </div>
                  <p class="text-xs text-gray-400">
                    Use este link em suas campanhas, automações e fluxos para garantir o rastreamento correto dos eventos.
                  </p>
                </div>

                <!-- Checkboxes para eventos -->
                <div class="space-y-4 pt-2">
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      id="tiktokEnablePageView"
                      v-model:checked="selectedIntegration.data.enablePageView"
                    />
                    <label
                      for="tiktokEnablePageView"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                    >
                      Habilitar evento ViewContent (visualização/lead)
                    </label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      id="tiktokEnableInitiateCheckout"
                      v-model:checked="selectedIntegration.data.enableInitiateCheckout"
                    />
                    <label
                      for="tiktokEnableInitiateCheckout"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                    >
                      Habilitar evento InitiateCheckout
                    </label>
                  </div>
                </div>
              </div>

              <!-- Seção específica para Kwai com mensagem e link do bot -->
              <div v-if="selectedIntegration?.provider === IntegrationProviderEnum.KWAI" class="space-y-6">
                <!-- Mensagem informativa sobre o rastreamento -->
                <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <h4 class="text-sm font-medium text-blue-300 mb-1">Configuração do Rastreamento</h4>
                      <p class="text-xs text-blue-200/80 leading-relaxed">
                        O Kwai não usa Access Token/Pixel ID — a atribuição é feita pelo link de rastreamento do bot abaixo (que carrega o clique do anúncio) combinado com os códigos de evento configurados no Kwai Ads Manager.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Campo do link do bot -->
                <div class="space-y-3">
                  <Label class="text-sm font-medium text-white">Link do Bot para Rastreamento</Label>
                  <div class="flex items-center space-x-2">
                    <div class="flex-1 relative">
                      <Input
                        :model-value="botTrackingLink"
                        readonly
                        class="pr-10 text-gray-300 cursor-pointer select-all"
                        @click="selectBotLink"
                      />
                      <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      @click="copyBotLink"
                      class="px-3 py-2 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all duration-200"
                    >
                      <svg v-if="!linkCopied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <svg v-else class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span class="ml-1.5 text-xs font-medium">
                        {{ linkCopied ? 'Copiado!' : 'Copiar' }}
                      </span>
                    </Button>
                  </div>
                  <p class="text-xs text-gray-400">
                    Use este link em suas campanhas do Kwai Ads para garantir que o clique (callback) seja capturado e o rastreamento funcione.
                  </p>
                </div>

                <!-- Purchase (sempre ativo) -->
                <div class="space-y-2">
                  <Label for="kwaiPurchaseEventType" class="text-sm font-medium">Código do Evento de Compra</Label>
                  <Input
                    id="kwaiPurchaseEventType"
                    type="number"
                    placeholder="event_type do evento de Compra no Kwai Ads Manager"
                    v-model="selectedIntegration.data.purchaseEventType"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <!-- PageView / ViewContent -->
                <div class="flex items-center space-x-2">
                  <Checkbox
                    id="kwaiEnablePageView"
                    v-model:checked="selectedIntegration.data.enablePageView"
                  />
                  <label
                    for="kwaiEnablePageView"
                    class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                  >
                    Habilitar evento de visualização/lead
                  </label>
                </div>
                <div v-if="selectedIntegration.data.enablePageView" class="space-y-2 pl-6">
                  <Label for="kwaiPageViewEventType" class="text-sm font-medium">Código do Evento de Visualização</Label>
                  <Input
                    id="kwaiPageViewEventType"
                    type="number"
                    placeholder="event_type do evento de visualização no Kwai Ads Manager"
                    v-model="selectedIntegration.data.pageViewEventType"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <!-- InitiateCheckout -->
                <div class="flex items-center space-x-2">
                  <Checkbox
                    id="kwaiEnableInitiateCheckout"
                    v-model:checked="selectedIntegration.data.enableInitiateCheckout"
                  />
                  <label
                    for="kwaiEnableInitiateCheckout"
                    class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                  >
                    Habilitar evento InitiateCheckout
                  </label>
                </div>
                <div v-if="selectedIntegration.data.enableInitiateCheckout" class="space-y-2 pl-6">
                  <Label for="kwaiInitiateCheckoutEventType" class="text-sm font-medium">Código do Evento InitiateCheckout</Label>
                  <Input
                    id="kwaiInitiateCheckoutEventType"
                    type="number"
                    placeholder="event_type do evento de InitiateCheckout no Kwai Ads Manager"
                    v-model="selectedIntegration.data.initiateCheckoutEventType"
                    class="rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              <Button
                type="submit"
                class="text-white"
                :disabled="isSaving || !selectedIntegration?.data"
              >
                <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
                {{ selectedIntegration?.buttonText || 'Salvar' }}
              </Button>
            </div>
          </form>
        </div>
        <Loader2 v-else class="w-10 h-10 animate-spin text-white mx-auto mt-4" />
      </DialogScrollContent>
    </Dialog>
  </div>

  <!-- ── Lightbox ──────────────────────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="x-lightbox">
      <div
        v-if="lightboxImage"
        @click="lightboxImage = null"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-6 cursor-zoom-out"
        style="background: rgba(0,0,0,0.88); backdrop-filter: blur(6px);"
      >
        <div
          class="relative w-full max-w-3xl"
          @click.stop
        >
          <!-- Chrome bar -->
          <div class="bg-zinc-800 rounded-t-xl px-4 py-2.5 flex items-center gap-1.5 border border-white/[0.08] border-b-zinc-700">
            <div class="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
            <span class="ml-2.5 text-xs font-mono text-zinc-500">ads.x.com</span>
            <button
              @click="lightboxImage = null"
              class="ml-auto w-6 h-6 rounded-md bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-colors duration-150 cursor-pointer"
            >
              <svg class="w-3.5 h-3.5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <!-- Image -->
          <img
            :src="lightboxImage"
            alt="Screenshot"
            class="w-full h-auto rounded-b-xl border border-white/[0.08] border-t-0 block"
            style="max-height: 80vh; object-fit: contain;"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
  <!-- ── /Lightbox ─────────────────────────────────────────────────── -->

</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogScrollContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { Checkbox } from '@/components/ui/checkbox';
import { useBot } from '@/composables/useBot';
import { useIntegration } from '@/composables/useIntegration';
import { IntegrationProviderEnum } from '@/enums/integrations/IntegrationProviderEnum';

// ── Estado reativo ─────────────────────────────────────────────────
const { selectedBot } = useBot()
const { getByProvider, save } = useIntegration()
const showDialog = ref(false);
const selectedIntegration = ref<any>(null);
const isLoading = ref(false);
const isSaving = ref(false);
const linkCopied = ref(false);

// Tutorial X
const showXTutorial = ref(false);
const lightboxImage = ref<string | null>(null);

const botTrackingLink = computed(() => {
  return `https://bot.mybotexpress.com/${selectedBot.value?.uuid}`
});

// ── Tutorial steps ─────────────────────────────────────────────────
const xTutorialSteps = [
  {
    title: 'Acesse o Gerenciador de Eventos',
    description: 'Em ads.x.com, no menu lateral em "Ferramentas", clique em "Gerenciador de eventos".',
    image: '/img/integrations/x/x-events-manager.png',
  },
  {
    title: 'Clique em "Install Pixel"',
    description: 'Na tela do gerenciador, clique no botão "Install Pixel" no canto superior direito.',
    image: '/img/integrations/x/x-install-pixel.png',
  },
  {
    title: 'Selecione "Manual" e continue',
    description: 'Escolha "Manual" para instalar via código ou Conversion API e clique em "Continuar".',
    image: '/img/integrations/x/x-manual.png',
  },
  {
    title: 'Selecione "Conversion API"',
    description: 'Escolha "Conversion API" para enviar eventos diretamente do servidor e clique em "Continuar".',
    image: '/img/integrations/x/x-conversion-api.png',
  },
  {
    title: 'Copie o Access Token e o Pixel ID',
    description: 'Copie o Access Token gerado. O Pixel ID aparece na URL do endpoint, após "/conversions/".',
    image: '/img/integrations/x/x-access-token-pixel-id.png',
  },
] as const;

// ── Integrações ────────────────────────────────────────────────────
const integrations = ref([
  {
    provider: IntegrationProviderEnum.UTMIFY,
    name: 'UTMify',
    logo: '/assets/integrations/utmify-logo.jfif',
    description: 'Gere UTMs automaticamente e rastreie suas campanhas',
    connected: false,
    fields: [
      {
        id: 'accessToken',
        label: 'Token de acesso',
        type: 'text',
        placeholder: 'Digite seu token de API',
        color: '#6b21a8'
      }
    ],
    data: {},
    buttonText: 'Salvar'
  },
  {
    provider: IntegrationProviderEnum.TWITTER,
    name: 'X',
    logo: '/assets/integrations/x.png',
    description: 'Rastreie conversões e otimize campanhas no X',
    connected: false,
    fields: [
      {
        id: 'accessToken',
        label: 'Access Token',
        type: 'text',
        placeholder: 'Access Token no X Ads Manager',
        color: '#000000'
      },
      {
        id: 'pixelId',
        label: 'Pixel ID',
        type: 'text',
        placeholder: 'Pixel ID no X Ads Manager',
        color: '#000000'
      },
    ],
    data: {
      enablePageView: false,
      enableInitiateCheckout: false
    },
    buttonText: 'Salvar'
  },
  {
    provider: IntegrationProviderEnum.META,
    name: 'Pixel Meta',
    logo: '/assets/integrations/meta-logo-icon.png',
    description: 'Monitore conversões e otimize suas campanhas',
    connected: false,
    fields: [
      {
        id: 'accessToken',
        label: 'Token de acesso',
        type: 'text',
        placeholder: 'Digite seu token de API',
        color: '#6b21a8'
      },
      {
        id: 'pixelId',
        label: 'Pixel ID',
        type: 'text',
        placeholder: 'Digite seu Pixel ID',
        color: '#6b21a8'
      }
    ],
    data: {
      enablePageView: false,
      enableInitiateCheckout: false
    },
    buttonText: 'Salvar'
  },
  {
    provider: IntegrationProviderEnum.TIKTOK,
    name: 'TikTok Ads',
    logo: '/img/integrations/tiktok/tiktok-icon.png',
    description: 'Rastreie conversões e otimize campanhas no TikTok',
    connected: false,
    fields: [
      {
        id: 'accessToken',
        label: 'Access Token',
        type: 'text',
        placeholder: 'Access Token no TikTok Events Manager',
        color: '#000000'
      },
      {
        id: 'pixelCode',
        label: 'Pixel Code',
        type: 'text',
        placeholder: 'Pixel Code no TikTok Events Manager',
        color: '#000000'
      }
    ],
    data: {
      enablePageView: false,
      enableInitiateCheckout: false
    },
    buttonText: 'Salvar'
  },
  {
    provider: IntegrationProviderEnum.KWAI,
    name: 'Kwai Ads',
    logo: '/img/integrations/kwai-icon.svg',
    description: 'Rastreie conversões e otimize campanhas no Kwai',
    connected: false,
    fields: [],
    data: {
      enablePageView: false,
      enableInitiateCheckout: false
    },
    buttonText: 'Salvar'
  }
]);

// Kwai temporariamente oculto do grid de integrações
const visibleIntegrations = computed(() => integrations.value.filter(integration => integration.provider !== IntegrationProviderEnum.KWAI))

// ── Métodos ────────────────────────────────────────────────────────
const openIntegrationDialog = async (integration: typeof integrations.value[0]) => {
  const fields: Record<string, string> = {}
  for (const field of integration.fields) {
    fields[field.id] = ''
  }
  selectedIntegration.value = integration
  showDialog.value = true
  showXTutorial.value = false
  lightboxImage.value = null
  isLoading.value = true
  selectedIntegration.value!.data = (await getByProvider(integration.provider))?.data ?? fields
  isLoading.value = false
};

const copyBotLink = async () => {
  try {
    await navigator.clipboard.writeText(botTrackingLink.value);
    linkCopied.value = true;
    toast({
      title: 'Link copiado com sucesso!',
      description: 'O link do bot foi copiado para a área de transferência.',
      class: 'bg-emerald-500 text-white',
      duration: 2000
    });
    setTimeout(() => { linkCopied.value = false; }, 2000);
  } catch {
    toast({
      title: 'Erro ao copiar',
      description: 'Não foi possível copiar o link. Tente selecionar e copiar manualmente.',
      class: 'bg-red-500 text-white',
      duration: 3000
    });
  }
};

const selectBotLink = (event: Event) => {
  const input = event.target as HTMLInputElement;
  input.select();
};

const handleSave = async () => {
  const data = selectedIntegration.value!.data
  if (!selectedIntegration.value) return;
  isSaving.value = true;
  await save({ data: data, provider: selectedIntegration.value!.provider })
  isSaving.value = false;
  toast({
    title: 'Integração salva com sucesso',
    class: 'bg-green-500 text-white',
    duration: 1500
  });
  showDialog.value = false;
};
</script>

<style scoped>
/* ── Tutorial toggle ───────────────────────────────────────────── */
.x-tutorial-toggle {
  background: rgba(24, 24, 27, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.07);
}
.x-tutorial-toggle:hover,
.x-tutorial-toggle--open {
  background: rgba(39, 39, 42, 0.9);
  border-color: rgba(255, 255, 255, 0.13);
}

/* ── Step connector (dotted) ───────────────────────────────────── */
.x-step-connector {
  background: repeating-linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.12) 0px,
    rgba(255, 255, 255, 0.12) 3px,
    transparent 3px,
    transparent 7px
  );
}

/* ── Screenshot hover border ───────────────────────────────────── */
.x-screenshot-btn:hover {
  border-color: rgba(16, 185, 129, 0.25);
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.1);
}

/* ── Scrollbar ─────────────────────────────────────────────────── */
.x-steps-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(63, 63, 70, 0.8) transparent;
}
.x-steps-scroll::-webkit-scrollbar {
  width: 4px;
}
.x-steps-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.x-steps-scroll::-webkit-scrollbar-thumb {
  background: rgba(63, 63, 70, 0.8);
  border-radius: 2px;
}

/* ── Tutorial accordion transition ────────────────────────────── */
.x-tutorial-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.x-tutorial-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.x-tutorial-enter-from,
.x-tutorial-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Lightbox transition ───────────────────────────────────────── */
.x-lightbox-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.x-lightbox-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.x-lightbox-enter-from,
.x-lightbox-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* ── Dialog page transitions ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
